/** Node Detail renderer registry (spec §2/§20/§21) — the ONLY place a
 * node type gets mapped to its detail-panel implementation. Adding a
 * real renderer for Video/Audio/Dialogue/Voice/Prompt/Publish later
 * (PHASE C) is exactly one new entry here, same "single registry, no
 * scattered switch statements" convention `nodeRegistry.tsx` already
 * established for node creation. Every node type not listed falls back
 * to `GenericDetailRenderer` — never renders nothing. */

import type { ComponentType } from 'react'
import type { NodeDetailRendererProps } from '../types'
import GenericDetailRenderer from './GenericDetailRenderer'
import ImageAssetDetailRenderer from './ImageAssetDetailRenderer'

const DETAIL_RENDERERS: Record<string, ComponentType<NodeDetailRendererProps>> = {
  image_asset: ImageAssetDetailRenderer,
}

export function getNodeDetailRenderer(nodeType: string): ComponentType<NodeDetailRendererProps> {
  return DETAIL_RENDERERS[nodeType] ?? GenericDetailRenderer
}

/** Scene/group nodes have their own inline collapse affordance already
 * (`SceneNode`'s header chevron) — they never get a Node Detail Shell
 * panel of their own. Every Media Card family (`media`'s `ImageAssetNode`,
 * `generator`'s `GenerateImageNode`, `video_generator`'s
 * `GenerateVideoNode` — VISTOERA_CONTENT_CANVAS_MEDIA_CARD_AND_GENERATION_INTERACTION_V2
 * §5/§13/§33/§46) now disambiguates single-vs-double click ON THE MEDIA
 * BODY ITSELF via `useSingleOrDoubleClick` (so a real double click never
 * also fires the single-click side effect first) and, for `generator`/
 * `video_generator`, renders its own attached-below Generation Inspector
 * inline rather than through the generic below-node `NodeDetailShell` —
 * so none of the three ever gets a SECOND, CanvasBoard-driven toggle/
 * panel from this generic path.
 *
 * `text_asset` (VISTOERA_CANVAS_TEXT_ASSET_INLINE_EDITOR_CANONICAL_CARD_P0_V1)
 * joins this exclusion — it owns its OWN inline expand/edit shell inside
 * `TextAssetNode` (single-click body expand + in-card textarea), so a second
 * CanvasBoard-driven `NodeDetailShell` toggle would fight the same click.
 *
 * `video_media` (`VideoAssetNode`) now mirrors `video_generator` —
 * inline `.pcv-gen-inspector` below the preview, never the portaled
 * `NodeDetailShell` that visually overlapped the media card.
 *
 * `motion_processor` (`MotionReconstructionNode`) joins this exclusion —
 * it already renders its own ALWAYS-visible inline `.pcv-gen-inspector`
 * below the media body, so without this exclusion a plain click ALSO
 * opened a second, redundant `NodeDetailShell`.
 *
 * `tryon_generator` (`GenerateTryOnNode`) is a Canonical Generation Card
 * with its own inline expand/collapse body (Result always visible;
 * Assets/Parameters/Prompt/Footer gated on `isDetailExpanded`) — same
 * exclusion rationale as `generate_creative` / `script_generation`.
 *
 * Every OTHER node type is unaffected — plain click still toggles the
 * CanvasBoard-rendered `NodeDetailShell`/`*DetailRenderer` exactly as
 * before. */
export function nodeTypeSupportsDetailPanel(family: string): boolean {
  return (
    family !== 'scene' &&
    family !== 'media' &&
    family !== 'generator' &&
    family !== 'video_generator' &&
    family !== 'video_media' &&
    family !== 'voice_generator' &&
    family !== 'audio_generator' &&
    family !== 'text_asset' &&
    family !== 'motion_processor' &&
    family !== 'tryon_generator' &&
    // VISTOERA_CANVAS_SCRIPT_GENERATION_NODE_AND_CREATIVE_ASSET_CONTEXT_
    // LINKING_V1 — `script_generation` owns its OWN always-visible
    // "查看完整剧本" Modal (`ScriptFullDetailModal` in
    // `ScriptGenerationNode.tsx`); a second, CanvasBoard-driven generic
    // `NodeDetailShell` below the node would just show a redundant raw
    // field dump, exactly the `motion_processor`/`tryon_generator`
    // rationale above.
    family !== 'script_generation' &&
    // VISTOERA_GENERATE_CREATIVE_CANONICAL_GENERATION_CARD_UI_DIVERGE_
    // TRIPLET_AND_SINGLE_EXPANSION_V3 — `generate_creative` now renders
    // its OWN inline expand/collapse body (Result always visible;
    // Assets/Parameters/Prompt gated on `isDetailExpanded`, exactly the
    // `script_generation` precedent above) rather than a second,
    // CanvasBoard-driven generic `NodeDetailShell`.
    family !== 'generate_creative' &&
    // VISTOERA_CANVAS_STORYBOARD_GENERATION_NODE_SCRIPT_CONTEXT_AND_SHOT_
    // ARTIFACT_V1 — Wide card owns its own expand/collapse + Shot Detail
    // Modal; exclude generic NodeDetailShell like script/creative.
    family !== 'storyboard_generation' &&
    // Canonical Generate Text card owns its own expand/collapse + result UI;
    // exclude generic NodeDetailShell like script/creative/storyboard.
    family !== 'text_generator' &&
    // VistoeraTASK-00165 — `audio_asset` owns inline expand + Audio Editor
    // entry; never the generic metadata `NodeDetailShell`.
    family !== 'audio_asset' &&
    // VistoeraTASK-00161 — 服装视频换装 (`ApparelVideoReplicationNode`) is a
    // Canonical Generation Card with its own inline expand/collapse body
    // (Result [video] always visible; Assets/Parameters/Prompt/Footer
    // gated on `isDetailExpanded`) — same exclusion rationale as
    // `tryon_generator` above.
    family !== 'apparel_video_generator'
  )
}
